import React from "react";
import moment from "moment/moment";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { serverFormatSeconds } from "../../../../utils/dates";
import getTotalReceivingUnitsLabel from "../Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import Header from "../../../wizard/Header";
import Progress from "../../../wizard/Progress";
import PdfGenerateLink from "../../../PdfGenerateLink";
import useTimer from "../../../../hooks/shipment/useTimer";
import LabelingOptionsModal from "../../../modals/LabelingOptionsModal";
import useItemLabelsModal from "../../../../hooks/useItemLabelsModal";
import { shipmentHasMobileLocationLabels } from "../../../../utils/shipments";
import WeightMismatchAlert from "../WeightMismatchAlert";

export default function Confirmation({ shipment, project, onClose }) {
  const navigate = useNavigate();
  const itemLabelsModal = useItemLabelsModal();
  const totalPieces = shipment?.totalPieces || [];
  const hasMobileLocationLabels = shipmentHasMobileLocationLabels(shipment);
  const start = moment(shipment.startTime, serverFormatSeconds);
  const end = moment(shipment.completeTime, serverFormatSeconds);
  const [minutes, seconds] = useTimer(start, end);
  const moveToStepOne = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", "shipment");
    navigate({ pathname, search: params.toString() });
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header title="Confirmation" info={getTotalReceivingUnitsLabel(totalPieces)} />
      <WeightMismatchAlert shipment={shipment} />
      <Box p={2}>
        <Box mt={3} display="flex" flexDirection="column" alignItems="center">
          <Box
            component="img"
            borderRadius={2}
            width="100%"
            maxWidth={360}
            src="/shipment-confirmation.webp"
          />
          <Typography mt={5} textAlign="center">
            Success! You recorded shipment <strong>{shipment.shipmentId}{shipment.revisionNumber > 0 ? `.R${shipment.revisionNumber}` : ''}</strong>
          </Typography>
          {!shipment.draft && shipment.shipmentStatus !== 'onsite_inventory' && (
            <>
              <Typography mt={3}>
                You completed in:{" "}
                <b>
                  {minutes} min {seconds} seconds
                </b>
              </Typography>
            </>
          )}
          <Box pt={6}>
            <PdfGenerateLink
              resourceType="shipment_pdfs"
              resourceId={shipment.id}
              sx={{ textDecoration: "none", fontWeight: 500, fontSize: 16 }}
              label="Generate Receiving Report PDF"
            />
          </Box>
          <Box pt={3} pb={2} display="flex" alignItems="center" justifyContent="center" gap={1}>
            <Button
              color="primary"
              variant="text"
              sx={{ fontWeight: 500, fontSize: 16 }}
              onClick={() => itemLabelsModal.openForShipment(shipment.id)}
            >
              Generate Item Labels
            </Button>
            {hasMobileLocationLabels && (
              <>
                <Typography color="text.secondary" sx={{ fontWeight: 500, fontSize: 16 }}>
                  ||
                </Typography>
                <PdfGenerateLink
                  resourceType="shipment_mobile_location_labels_pdfs"
                  resourceId={shipment.id}
                  sx={{ textDecoration: "none", fontWeight: 500, fontSize: 16 }}
                  label="Location Labels"
                  regenerateLabel="Force Regenerate Location Labels"
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={2}>
        <Progress step={2} />
        <Box p={2}>
          <Box display="flex" justifyContent="space-between">
            <Button variant="outlined" color="inherit" onClick={moveToStepOne}>
              Edit Shipment
            </Button>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Box>
      {itemLabelsModal.isOpen && itemLabelsModal.shipmentId === shipment.id && (
        <LabelingOptionsModal onClose={itemLabelsModal.close} shipmentId={shipment.id} project={project} />
      )}
    </Box>
  );
}
